<template>
  <section class="nearby-sites-results" :class="{ desktop: $isDesktop }">
    <pu-campsite-link
      v-for="campsite in campsites"
      :key="campsite.id"
      v-observe-visibility="{
        callback: (isVisible) =>
          !$isDesktop && isVisible && $emit('campsite-changed', campsite.slug),
        intersection: {
          threshold: 0.75,
        },
        throttle: 1000,
      }"
      :exclude-query-params="['sa', 'ved']"
      :hierarchy-path="campsite.hierarchyPath"
      :slug="campsite.slug"
      class="mini-card-link"
      :class="{ active: activeCampsite === campsite.slug }"
      :data-slug="campsite.slug"
      @mouseenter.passive="
        $isDesktop && $emit('campsite-changed', campsite.slug)
      "
    >
      <slot name="minicard" :campsite="campsite">
        <mini-card v-bind="campsiteToMiniCard(campsite)" />
      </slot>
    </pu-campsite-link>
  </section>
</template>

<script lang="ts">
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'
import { getTransformObject } from '~/common/photos/transforms'
import { NearbyCampsite } from '../schemas'
import { MiniCardProps } from './mini-card/MiniCardProps'

@Component({
  emits: ['campsite-changed'],
})
class NearbyBookableList extends Vue {
  @Prop({ required: true })
  campsites: NearbyCampsite[]

  @Prop()
  activeCampsite: string | undefined

  campsiteToMiniCard(campsite: NearbyCampsite): MiniCardProps {
    return {
      slug: campsite.slug,
      name: campsite.name,
      rating: campsite.rating ? campsite.rating * 2 : 0,
      rateCount: campsite.rateCount,
      hierarchyStringShort: campsite.hierarchyStringShort,
      distance: campsite.distance,
      leadPrice: campsite.leadPrice,
      leadPriceAdults: campsite.leadPriceAdults,
      leadPriceNights: campsite.leadPriceNights,
      price: campsite.price,
      categories: campsite.categories.map((cat) => ({
        ...cat,
        isPositive: true,
      })),
      primaryPhotoUrl: campsite.primaryPhoto
        ? {
            caption: campsite.primaryPhoto?.caption || '',
            url: getTransformObject(
              'nearbySite',
              campsite.primaryPhoto.url.masterImage,
            ),
          }
        : undefined,
    }
  }
}

export default toNative(NearbyBookableList)
</script>

<style scoped>
.nearby-sites-results {
  display: flex;
  flex-wrap: nowrap;
  justify-content: flex-start;
  overflow-x: auto;
  overflow-y: visible;
  padding: 2em 0 4em;
  scroll-behavior: smooth;
  z-index: 1;
  position: relative;
  scroll-snap-type: x mandatory;
}

@supports (scrollbar-width: none) {
  .nearby-sites-results {
    scrollbar-width: none;
  }

  .nearby-sites-results.desktop {
    scrollbar-width: thin;
  }
}

@supports selector(::-webkit-scrollbar) {
  .nearby-sites-results:not(.desktop)::-webkit-scrollbar {
    display: none;
  }
}

.nearby-sites-results:after {
  width: 1.4em;
  content: '';
  flex: 0 0 auto;
}

.mini-card-link {
  align-self: stretch;
  flex: 0 0 auto;
  margin: 0 0 0 1.4em;
  scroll-margin: 0 1.4em;
  scroll-snap-align: start;
  scroll-snap-stop: normal;
  text-decoration: none;
  width: 60vw;
  width: calc((100% - 2.8em) / 1.5);
}

.mini-card-link:active {
  transform: scale(1.03);
  filter: brightness(0.8) drop-shadow(0 0.2em 0.3em #394a5a);
}

/* desktop & tablet */
.desktop .mini-card-link {
  width: 13em;
}
</style>
